import {
  protectedGet,
  protectedPatch,
  protectedPost,
  protectedDelete,
  protectedDeleteWithBody,
} from "./restUtil";
import { transformPlan } from "./planUtil";
import * as _ from "underscore";
import moment from "moment";
import {
  isCompleted,
  isDeleted,
  isCanceled,
  isMissed,
  isIssuesRescheduled,
} from "./sessionStatus";
import { fetchOwnStudentData } from "./users";
import queryString from "querystring";

export class StudentStatus {
  static PAYING = "paying";
  static PILOT = "pilot";
  static PILOT_COMPLETED = "pilot-completed";
  static GRADUATED = "graduated";
  static PAUSED = "paused";
  static SETUP = "setup";

  static getAllStudentStatuses() {
    return [
      StudentStatus.PAYING,
      StudentStatus.GRADUATED,
      StudentStatus.PAUSED,
      StudentStatus.PILOT,
      StudentStatus.PILOT_COMPLETED,
      StudentStatus.SETUP,
    ];
  }
}

export function isPilot(status) {
  return StudentStatus.PILOT === status;
}

export function isPaused(status) {
  return StudentStatus.PAUSED === status;
}

export function isGraduated(status) {
  return StudentStatus.GRADUATED === status;
}

export function isSetup(status) {
  return StudentStatus.SETUP === status;
}

export function isPilotCompleted(status) {
  return StudentStatus.PILOT_COMPLETED === status;
}

export function isPaying(status) {
  return StudentStatus.PAYING === status;
}

export function fetchStudents(args, onSuccess, onError) {
  let args_ = args || {};
  args_.sort = "name";
  return protectedGet(
    "/students",
    args_,
    function (resp) {
      if (!resp || !resp.list) {
        return onError("invalid response structure");
      }

      // TODO change the server to return required response structure
      const students = [];
      _.each(resp.list, s => {
        students.push({
          id: s.id,
          profile: {
            email: s.email,
            first_name: s.first_name,
            last_name: s.last_name,
            timezone: {
              id: s.timezone.id,
              utc_offset: s.timezone.utc_offset,
            },
            address: {
              city: s.city,
              country: s.country,
            },
          },
          student: {
            number_of_lessons: s.number_of_lessons,
          },
        });
      });

      return onSuccess({ list: students });
    },
    onError
  );
}

export function fetchStudent(args, onSuccess, onError) {
  return protectedGet(
    "/student/" + args.id,
    undefined,
    resp => {
      if (!resp) {
        return onError && onError("invalid response");
      }

      // TODO change the server to return required response structure
      const student = {
        id: resp.id,
        profile: {
          email: resp.profile.email,
          first_name: resp.profile.first_name,
          last_name: resp.profile.last_name,
          timezone: resp.profile.timezone,
          address: {
            city: resp.profile.city,
            country: resp.profile.country,
          },
        },
      };

      return onSuccess && onSuccess({ participant: student });
    },
    onError
  );
}

export function fetchStudentWithPlan(args, onSuccess, onError) {
  return protectedGet(
    "/student/" + args.id,
    undefined,
    resp => {
      const student = resp;

      return protectedGet(
        "/student/" + args.id + "/plan/current",
        undefined,
        planResp => {
          if (planResp && planResp.plan) {
            student.first_name = resp.profile.first_name;
            student.last_name = resp.profile.last_name;
            student.plan = transformPlan(planResp.plan);
            if (!student.sessions) {
              student.sessions = planResp.plan.sessions;
            }
          }
          return onSuccess && onSuccess(student);
        },
        onError
      );
    },
    onError
  );
}

export function fetchStudentActivity(args, onSuccess, onError) {
  let { from, to } = args;
  const params = queryString.stringify({ from, to });

  return protectedGet(
    `/student/activity?${params}`,
    undefined,
    resp => {
      if (!resp) {
        return onError && onError("invalid response");
      }

      return onSuccess && onSuccess(resp);
    },
    onError
  );
}

export function deleteStudentPlan(args, onSuccess, onError) {
  return protectedDelete(
    "/student/" + args.student_id + "/plan",
    {
      plan_id: args.plan_id,
    },
    onSuccess,
    onError
  );
}

export function updateStudentLevel(args, onSuccess, onError) {
  return protectedPatch(
    "/student/" + args.student_id + "/level",
    {
      score: args.score,
    },
    onSuccess,
    onError
  );
}

export function fetchStudentSessions(args, onSuccess, onErrors) {
  return protectedGet(
    "/student/" + args.student_id + "/sessions/lesson",
    undefined,
    function (resp) {
      if (!resp || !resp.list) {
        return onErrors && onErrors("invalid response structure");
      }

      let sessions = _.sortBy(
        _.filter(resp.list, function (s) {
          if (args.type === "upcoming") {
            return (
              moment().startOf("hour").isSameOrBefore(moment(s.scheduled), "hour") &&
              !isCompleted(s.status) &&
              !isCanceled(s.status) &&
              !isMissed(s.status) &&
              !isDeleted(s.status) &&
              !isIssuesRescheduled(s.status)
            );
          } else if (args.type === "completed") {
            return moment().utc().isAfter(moment(s.scheduled)) && isCompleted(s.status);
          } else {
            return false;
          }
        }),
        function (s) {
          const ms = moment(s.scheduled).toDate().getTime();
          return args.type === "upcoming" ? ms : -ms;
        }
      );
      return onSuccess(sessions);
    },
    onErrors
  );
}

export function fetchStudentUpcomingSessions(args, onSuccess, onErrors) {
  return fetchStudentSessions(
    {
      student_id: args.student_id,
      type: "upcoming",
    },
    onSuccess,
    onErrors
  );
}

export function fetchStudentCompletedSessions(args, onSuccess, onErrors) {
  return fetchStudentSessions(
    {
      student_id: args.student_id,
      type: "completed",
    },
    onSuccess,
    onErrors
  );
}

export function fetchStudentCompletedFeedback(args, onSuccess, onErrors) {
  protectedGet(
    "/student/" + args.student_id + "/sessions/lesson/completed/feedback",
    {
      empty: args.empty,
      page: args.page,
      size: args.size,
    },
    resp => {
      if (!resp || !resp.list) {
        onErrors && onErrors("invalid response structure");
      }
      onSuccess(resp);
    },
    onErrors
  );
}

export function isFirstSession(args, onSuccess, onErrors) {
  return fetchOwnStudentData(
    args,
    resp => {
      return onSuccess && onSuccess({ assessment_session: resp.number_of_lessons === 0 });
    },
    onErrors
  );
}

export function patchStudentProfile(args, onSuccess, onErrors) {
  return protectedPatch("/student", args, onSuccess, onErrors);
}

export function fetchStudentProfile(args, onSuccess, onErrors) {
  return protectedGet("/student", undefined, onSuccess, onErrors);
}

export function fetchStudentProfileAsync(args) {
  return new Promise((resolve, reject) => {
    fetchStudentProfile(args, resolve, reject);
  });
}

export function setupCompleted(args, onSuccess, onError) {
  return protectedPost("/student/setup/completed", undefined, onSuccess, onError);
}

// TODO should it be on the server?
const RECENT_STUDENTS_NAME = "Talaera_recent_students";
const RECENT_STUDENTS_NUMBER_OF_ITEMS = 5;

export function addItemToRecentStudents(item) {
  let recentStudents = getRecentStudents();

  let itemIndex = _.findIndex(recentStudents, st => st.id === item.id);
  if (itemIndex > -1) {
    // If the item is already in recent students, then just move the item to the top.
    // Note that `splice` has the following syntax splice(index, howmany, el1, /*...,*/ elN), where
    // - `index` is the position in the array you want to start removing elements from,
    // - `howmany` is how many elements you want to remove from index,
    // - `el1`, ..., `elN` are elements you want to insert from position index.
    // It also returns removed elements.
    recentStudents.splice(0, 0, recentStudents.splice(itemIndex, 1)[0]);
  } else {
    if (recentStudents.length > RECENT_STUDENTS_NUMBER_OF_ITEMS - 1) {
      recentStudents = _.first(recentStudents, RECENT_STUDENTS_NUMBER_OF_ITEMS - 1);
    }
    recentStudents.unshift({
      id: item.id,
      first_name: item.profile ? item.profile.first_name : item.first_name,
      last_name: item.profile ? item.profile.last_name : item.last_name,
    });
  }

  localStorage.setItem(RECENT_STUDENTS_NAME, JSON.stringify(recentStudents));
  return recentStudents;
}

export function getRecentStudents() {
  const recentStudents = localStorage.getItem(RECENT_STUDENTS_NAME);
  return recentStudents ? JSON.parse(recentStudents) : [];
}

export function getTags(args, onSuccess, onError) {
  return protectedGet("/student/tags", {}, onSuccess, onError);
}

export function addTags(args, onSuccess, onError) {
  return protectedPost(
    "/student/tags",
    {
      tags: args.tags,
    },
    onSuccess,
    onError
  );
}

export function removeTags(args, onSuccess, onError) {
  return protectedDeleteWithBody(
    "/student/tags",
    {
      tags: args.tags,
    },
    onSuccess,
    onError
  );
}
